import React from "react";
import { ArchiveBox, CaretRight } from "@phosphor-icons/react";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useLocale } from "lib/hooks/useLocale";
import { CheckoutPlan } from "lib/types/subscriptions";
import { currencyFormatter } from "lib/utils/formatters";
import { classNames } from "lib/utils/helpers";
import {
  DEFAULT_OFFER_PILL,
  DEFAULT_RING,
  DEFAULT_STRIP_TEXT,
  formatDeliveries,
  formatDiscount,
  formatDuration,
  formatFrequency,
  formatSavings,
  ribbonGradient,
} from "lib/utils/subscriptions/planFormatters";

interface PlanRowProps {
  plan: CheckoutPlan;
  isSelected: boolean;
  isDisabled?: boolean;
  onSelect?: (planId: string) => void;
  variant?: "selectable" | "summary";
  hasMorePlans?: boolean;
}
/* The selected plan is the merchant's brand colour: a full-strength ring over a 6% fill. */
const PRIMARY = "var(--flo-primary-dark-color)";
const selectedFill = (accent: string) => `color-mix(in srgb, ${accent} 6%, #FFFFFF)`;

const DISCOUNT_PILL_SHADOW =
  "shadow-[0_0_0_0.5px_rgba(9,9,11,0.12),0_15px_11px_-6px_rgba(9,9,11,0.06),0_2px_9px_-6px_rgba(9,9,11,0.48),0_1px_2px_rgba(10,13,18,0.06),inset_0_0_0_0.5px_rgba(9,9,11,0.12),inset_0_7px_2px_rgba(255,255,255,0.15)]";

const TILE_SHADOW = "0 1px 2px rgba(10,13,18,0.05)";
const selectedRing = (accent: string) => `0 0 0 1.5px inset ${accent}`;
const ROW_RING = "0 0 0 1px rgba(0,0,0,0.08) inset";
const RIBBON_VISIBLE_HEIGHT = 24;
const SUMMARY_BORDER = "var(--flo-gray-light-color)";

const PlanRow: React.FC<PlanRowProps> = ({
  plan,
  isSelected,
  isDisabled = false,
  onSelect,
  variant = "selectable",
  hasMorePlans = false,
}) => {
  const { t } = useLocale();
  const {
    state: { checkoutMetadata },
  } = useMerchantContext();

  const config = checkoutMetadata?.uiAttributes?.subscriptionPlanConfig;
  const card = config?.subscriptionCard;
  // Unstyled plan tiles follow the payment tile, so both look the same until the merchant overrides them.
  const paymentCard = checkoutMetadata?.uiAttributes?.paymentTileConfig?.layoutUI?.paymentCard;
  const borderRadius = paymentCard?.borderRadius;

  const isSummary = variant === "summary";
  const isInteractive = Boolean(onSelect) && !isDisabled;

  const frequency = formatFrequency(plan, t);
  const duration = formatDuration(plan, t);
  const deliveries = formatDeliveries(plan, t);
  const discount = formatDiscount(plan, t);
  const savings = formatSavings(plan, t);
  const { total, originalTotal } = plan.pricing;

  const showRecommendedBadge = plan.isRecommended && !isSummary;
  const recommendedLabel = config?.recommendedLabel ?? t("subscription_recommended");

  const accentColor = card?.highlightBackgroundColor || DEFAULT_RING;
  const isRinged = isSelected && !isSummary;
  const ribbonOverlap = borderRadius ?? 12;

  const ring = isSummary
    ? hasMorePlans
      ? `0 0 0 1.5px ${accentColor} inset, ${TILE_SHADOW}`
      : `0 0 0 1px ${SUMMARY_BORDER} inset`
    : isRinged
    ? selectedRing(PRIMARY)
    : ROW_RING;

  const style = {
    ...(borderRadius === undefined ? {} : { borderRadius }),
    ...(ring ? { boxShadow: ring } : {}),
    ...(card?.backgroundColor && !isSelected ? { backgroundColor: card.backgroundColor } : {}),
    ...(isRinged ? { backgroundColor: selectedFill(PRIMARY) } : {}),
  } as React.CSSProperties;

  const titleColor = card?.textColor || paymentCard?.textColor;
  const iconColor = card?.iconColor || paymentCard?.iconColor;

  const renderTitle = () => (
    <span className="flex flex-wrap items-baseline gap-x-1 h-6">
      <span
        className={classNames(
          isSummary ? "text-sm font-medium" : "text-text-sm-semibold",
          "whitespace-nowrap text-zinc-800/80",
        )}
        style={titleColor ? { color: titleColor } : undefined}>
        {t("subscription_every")} {frequency}
      </span>
      {duration ? (
        <span className={classNames("whitespace-nowrap", isSummary ? "leading-5" : "leading-6")}>
          <span
            className={classNames(
              isSummary ? "text-sm font-normal" : "text-text-sm-medium",
              "text-zinc-800/40",
            )}>
            {t("subscription_for")}{" "}
          </span>
          <span
            className={classNames(
              isSummary ? "text-sm font-medium" : "text-text-sm-semibold",
              "whitespace-nowrap text-zinc-800/80",
            )}
            style={titleColor ? { color: titleColor } : undefined}>
            {duration}
          </span>
        </span>
      ) : (
        <span className="whitespace-nowrap text-text-xxs-regular text-coal-light">
          {t("subscription_until_cancelled")}
        </span>
      )}
    </span>
  );

  return (
    <div className={classNames("relative flex flex-col", isDisabled && !isSummary ? "opacity-60" : "")}>
      {showRecommendedBadge && Boolean(recommendedLabel) && (
        <div
          className="flex items-start px-3 py-1"
          style={{
            height: RIBBON_VISIBLE_HEIGHT + ribbonOverlap,
            marginBottom: -ribbonOverlap,
            borderTopLeftRadius: borderRadius ?? 12,
            borderTopRightRadius: borderRadius ?? 12,
            background: ribbonGradient(card?.highlightBackgroundColor),
          }}>
          <span
            className="text-text-xs-semibold"
            style={{ color: card?.highlightTextColor || DEFAULT_STRIP_TEXT, opacity: 0.9 }}>
            {recommendedLabel}
          </span>
        </div>
      )}

      <div
        id={`subscription-plan-${plan.id}`}
        role={isInteractive ? (isSummary ? "button" : "radio") : undefined}
        aria-checked={isInteractive && !isSummary ? isSelected : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        onClick={() => isInteractive && onSelect?.(plan.id)}
        onKeyDown={(event) => {
          if (!isInteractive) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect?.(plan.id);
          }
        }}
        style={style}
        className={classNames(
          "relative z-[1] flex items-center rounded-md transition-[background-color,background-image,box-shadow]",
          isSummary ? "h-16 gap-2" : "gap-3",
          isSummary ? "bg-white py-0.5 pl-3 pr-3" : "px-4 py-2.5",
          isSummary || isRinged ? "" : "bg-white",
          isInteractive ? "cursor-pointer" : "",
          isInteractive
            ? isSummary
              ? "hover:bg-[image:linear-gradient(rgba(113,113,123,0.15),rgba(113,113,123,0.15))]"
              : "hover:bg-[image:linear-gradient(rgba(0,0,0,0.05),rgba(0,0,0,0.05))]"
            : "",
          isInteractive
            ? "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coal-light"
            : "",
        )}>
        {isSummary && (
          <div className="p-0.5">
            <ArchiveBox size={28} weight="duotone" color={iconColor} />
          </div>
        )}

        <div
          className={classNames(
            "flex min-w-0 flex-1 flex-col",
            isSummary ? "justify-center py-1" : "gap-0.5",
          )}>
          {renderTitle()}
          {Boolean(deliveries) && (
            <span
              className={
                isSummary
                  ? "text-xs font-normal text-zinc-800/80 opacity-60"
                  : "text-text-xs-medium text-coal-light"
              }
              style={titleColor ? { color: titleColor, opacity: 0.6 } : undefined}>
              {deliveries}
            </span>
          )}
        </div>

        <div
          className={classNames(
            "flex shrink-0 flex-col items-end py-1",
            isSummary ? "justify-center pl-2 gap-1" : "",
          )}>
          {total !== undefined && (
            <span
              className={classNames(
                isSummary ? "text-sm font-medium" : "text-text-sm-medium",
                "text-zinc-800/80",
              )}
              style={titleColor ? { color: titleColor } : undefined}>
              {currencyFormatter(total)}
            </span>
          )}

          {isSummary
            ? Boolean(savings) && <span className="text-text-xxs-semibold text-yay-dark">{savings}</span>
            : originalTotal !== undefined &&
              total !== undefined &&
              originalTotal > total && (
                <span className="text-text-xs-medium text-zinc-800/50 line-through">
                  {currencyFormatter(originalTotal)}
                </span>
              )}
        </div>

        {isSummary && (
          <CaretRight size={14} weight="bold" className="shrink-0 rotate-90 text-zinc-800/80 opacity-60" />
        )}
      </div>

      {!isSummary && Boolean(discount) && (
        <span
          className={classNames(
            "relative z-[2] -mt-2 mr-3 flex h-4 w-fit items-center self-end rounded-full px-1.5",
            "text-[10px] font-bold leading-4 text-white [text-shadow:0_0.5px_0_rgba(0,0,0,0.14)]",
            DISCOUNT_PILL_SHADOW,
          )}
          style={{ background: card?.offerColor || DEFAULT_OFFER_PILL }}>
          {discount}
        </span>
      )}
    </div>
  );
};

export default PlanRow;
